import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Users, Bot, TrendingUp, TrendingDown, Activity, BarChart3, Timer, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCenterAnalytics() {
  const [interactions, setInteractions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('today');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [interactionsRes, queuesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/call-center/interactions?page_size=500`, { headers }),
        axios.get(`${API}/call-center/queues`, { headers }),
        axios.get(`${API}/call-center/ai-agents`, { headers })
      ]);
      
      setInteractions(interactionsRes.data.interactions || []);
      setQueues(queuesRes.data.queues || []);
      setAiAgents(agentsRes.data.agents || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000);
    }
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  // Calculate metrics
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const filterByTimeRange = (items) => {
    return items.filter(item => {
      const itemDate = new Date(item.created_at);
      switch (timeRange) {
        case 'today':
          return itemDate >= todayStart;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return itemDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return itemDate >= monthAgo;
        default:
          return true;
      }
    });
  };

  const filteredInteractions = filterByTimeRange(interactions);

  const metrics = {
    totalCalls: filteredInteractions.length,
    inboundCalls: filteredInteractions.filter(i => i.direction === 'inbound').length,
    outboundCalls: filteredInteractions.filter(i => i.direction === 'outbound').length,
    missedCalls: filteredInteractions.filter(i => i.status === 'missed').length,
    voicemails: filteredInteractions.filter(i => i.interaction_type === 'voicemail').length,
    aiHandled: filteredInteractions.filter(i => i.handled_by_ai).length,
    avgDuration: filteredInteractions.length > 0 
      ? Math.round(filteredInteractions.reduce((sum, i) => sum + (i.duration_seconds || 0), 0) / filteredInteractions.length)
      : 0,
    completedCalls: filteredInteractions.filter(i => i.status === 'completed').length,
    answeredRate: filteredInteractions.length > 0
      ? Math.round((filteredInteractions.filter(i => i.status === 'completed').length / filteredInteractions.length) * 100)
      : 0
  };

  // Queue metrics
  const queueMetrics = queues.map(queue => {
    const queueInteractions = filteredInteractions.filter(i => i.queue_id === queue.id);
    return {
      ...queue,
      totalCalls: queueInteractions.length,
      avgWait: queueInteractions.length > 0
        ? Math.round(queueInteractions.reduce((sum, i) => sum + (i.wait_seconds || 0), 0) / queueInteractions.length)
        : 0,
      missedRate: queueInteractions.length > 0
        ? Math.round((queueInteractions.filter(i => i.status === 'missed').length / queueInteractions.length) * 100)
        : 0
    };
  });

  // AI Agent performance
  const aiPerformance = aiAgents.map(agent => ({
    ...agent,
    successRate: agent.total_interactions > 0
      ? Math.round((agent.successful_interactions / agent.total_interactions) * 100)
      : 0,
    escalationRate: agent.total_interactions > 0
      ? Math.round((agent.escalated_interactions / agent.total_interactions) * 100)
      : 0
  }));

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendUp, color = 'text-primary', subtitle }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end">
            <Icon className={`h-8 w-8 opacity-50 ${color}`} />
            {trend !== undefined && (
              <div className={`flex items-center text-xs mt-2 ${trendUp ? 'text-green-500' : 'text-red-500'}`}>
                {trendUp ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                {trend}%
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Call Center Analytics</h1>
          <p className="text-muted-foreground">Real-time performance dashboard</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading analytics...</div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <StatCard
              title="Total Calls"
              value={metrics.totalCalls}
              icon={Phone}
            />
            <StatCard
              title="Inbound"
              value={metrics.inboundCalls}
              icon={PhoneIncoming}
              color="text-green-600"
            />
            <StatCard
              title="Outbound"
              value={metrics.outboundCalls}
              icon={PhoneOutgoing}
              color="text-blue-600"
            />
            <StatCard
              title="Missed"
              value={metrics.missedCalls}
              icon={PhoneMissed}
              color="text-red-600"
            />
            <StatCard
              title="AI Handled"
              value={metrics.aiHandled}
              icon={Bot}
              color="text-purple-600"
              subtitle={`${metrics.totalCalls > 0 ? Math.round((metrics.aiHandled / metrics.totalCalls) * 100) : 0}% of total`}
            />
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Answer Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-4">
                  <div className="text-5xl font-bold text-green-600">{metrics.answeredRate}%</div>
                  <div className="text-sm text-muted-foreground pb-2">
                    {metrics.completedCalls} of {metrics.totalCalls} calls
                  </div>
                </div>
                <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${metrics.answeredRate}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Timer className="h-5 w-5" />
                  Avg Call Duration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-bold">{formatDuration(metrics.avgDuration)}</div>
                <p className="text-sm text-muted-foreground mt-2">
                  Average handling time
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Agent Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-3xl font-bold text-green-600">
                      {aiAgents.filter(a => a.status === 'available').length}
                    </div>
                    <p className="text-sm text-muted-foreground">Available</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-600">
                      {aiAgents.filter(a => a.status === 'on_call').length}
                    </div>
                    <p className="text-sm text-muted-foreground">On Call</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-400">
                      {aiAgents.filter(a => a.status === 'offline').length}
                    </div>
                    <p className="text-sm text-muted-foreground">Offline</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Queue Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Queue Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {queueMetrics.map(queue => (
                  <div key={queue.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{queue.name}</h4>
                      <Badge variant={queue.is_active ? 'default' : 'secondary'}>
                        {queue.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-xl font-bold">{queue.totalCalls}</div>
                        <div className="text-xs text-muted-foreground">Calls</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold">{queue.avgWait}s</div>
                        <div className="text-xs text-muted-foreground">Avg Wait</div>
                      </div>
                      <div>
                        <div className={`text-xl font-bold ${queue.missedRate > 20 ? 'text-red-600' : ''}`}>
                          {queue.missedRate}%
                        </div>
                        <div className="text-xs text-muted-foreground">Missed</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Agent Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Agent Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Agent</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-center py-3 px-4">Status</th>
                      <th className="text-right py-3 px-4">Total</th>
                      <th className="text-right py-3 px-4">Success Rate</th>
                      <th className="text-right py-3 px-4">Escalation Rate</th>
                      <th className="text-right py-3 px-4">Avg Handle Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiPerformance.map(agent => (
                      <tr key={agent.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{agent.name}</td>
                        <td className="py-3 px-4 text-muted-foreground capitalize">
                          {agent.agent_type?.replace(/_/g, ' ').replace('ai ', '')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant={agent.status === 'available' ? 'default' : 'secondary'}>
                            {agent.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">{agent.total_interactions || 0}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={agent.successRate >= 80 ? 'text-green-600' : agent.successRate >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                            {agent.successRate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={agent.escalationRate <= 10 ? 'text-green-600' : agent.escalationRate <= 20 ? 'text-yellow-600' : 'text-red-600'}>
                            {agent.escalationRate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">{agent.avg_handle_time_seconds || 0}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
    </Layout>
  );
}
